import React, { useMemo, useState, useEffect } from 'react';
import { Table, Form, Button, Spinner } from 'react-bootstrap';
import {
  useEntitiesByLocationMethodAndSampleName,
  useFindControlStrategyConfigurationByFullKey,
  getDefaultControlStrategyConfiguration,
  updateConfiguration
} from '../util/db';
import { serverTimestamp } from 'firebase/firestore';
import { computeInstrumentLimitsStats } from '../util/instrumentStats';
import { pickLatestControlStrategyConfig } from '../util/configSelection';
import InstrumentPoolingDisplay from './InstrumentPoolingDisplay';

const InstrumentLimits = ({ location, method, sampleName, parameter, summaries }) => {
  const { data } = useEntitiesByLocationMethodAndSampleName(location, method, sampleName);
  // State to track selection for pooling for each instrument
  const [poolingSelections, setPoolingSelections] = useState({});
  const [assignForPooling, setAssignForPooling] = useState({});
  const [config, setConfig] = useState({});
  const [currentInstrument, setCurrentInstrument] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Get the fullKey for the current instrument
  const fullKey = currentInstrument
    ? [location, method, sampleName, currentInstrument, parameter].join('|')
    : '';

  // Use the hook at the top level
  const { data: controlStrategyConfiguration } =
    useFindControlStrategyConfigurationByFullKey(fullKey);

  // Get list of instruments from data
  const instruments = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...new Set(data.map((item) => item.instrument).filter(Boolean))];
  }, [data]);

  // Initialize pooling selections when data changes
  useEffect(() => {
    if (data && data.length > 0) {
      const initialSelections = {};
      const initialAssignments = {};

      data.forEach((item) => {
        if (item.instrument) {
          initialSelections[item.instrument] = 'Y';
          initialAssignments[item.instrument] = 'Y';
        }
      });

      setPoolingSelections(initialSelections);
      setAssignForPooling(initialAssignments);

      // Set the first instrument to load its configuration
      if (instruments.length > 0) {
        setCurrentInstrument(instruments[0]);
      }
    }
  }, [data, instruments]);

  // Handle the control strategy configuration loading
  useEffect(() => {
    // When we get a configuration for the current instrument
    if (
      Array.isArray(controlStrategyConfiguration) &&
      controlStrategyConfiguration.length > 0 &&
      currentInstrument
    ) {
      // Update the config state
      setConfig((prev) => ({
        ...prev,
        [currentInstrument]: pickLatestControlStrategyConfig(controlStrategyConfiguration)
      }));

      // Move to the next instrument if there is one
      const currentIndex = instruments.indexOf(currentInstrument);
      if (currentIndex < instruments.length - 1) {
        setCurrentInstrument(instruments[currentIndex + 1]);
      }
    }
  }, [controlStrategyConfiguration, currentInstrument, instruments]);

  // Handle selection for pooling change
  const handleSelectionChange = (instrument, value) => {
    setPoolingSelections((prev) => ({
      ...prev,
      [instrument]: value
    }));
  };

  // Handle assign for pooling change
  const handleAssignmentChange = (instrument, value) => {
    setAssignForPooling((prev) => ({
      ...prev,
      [instrument]: value
    }));
  };

  // Calculate stats for each instrument
  const { instrumentStats, pooledValuesAll, pooledValuesSelected } = useMemo(
    () =>
      computeInstrumentLimitsStats({
        data,
        method,
        sampleName,
        location,
        parameter,
        summaries,
        poolingSelections,
        assignForPooling
      }),
    [data, method, sampleName, poolingSelections, assignForPooling, summaries, location, parameter]
  );

  return (
    <div>
      <div className="instrument-limits-table-container">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Instrument</th>
              <th>N</th>
              <th>Mean</th>
              <th>Standard Deviation</th>
              <th>RSD</th>
              <th>Variance</th>
              <th>UCL</th>
              <th>LCL</th>
              <th>Z-score Mean</th>
              <th>Z-score Variance</th>
              <th>Selection for Pooling</th>
              <th>Assign for Pooling</th>
            </tr>
          </thead>
          <tbody>
            {instrumentStats && instrumentStats.length > 0 ? (
              <>
                {instrumentStats
                  .sort((a, b) =>
                    a.instrument.localeCompare(b.instrument, undefined, {
                      numeric: true
                    })
                  )
                  .map((stat, index) => (
                    <tr key={`instrument-${index}`}>
                      <td>{stat.instrument}</td>
                      <td>{stat.n}</td>
                      <td>{stat.mean}</td>
                      <td>{stat.stdDev}</td>
                      <td>
                        {stat.rawMean > 0
                          ? ((stat.rawStdDev / stat.rawMean) * 100).toFixed(2)
                          : 'N/A'}
                      </td>
                      <td>{stat.variance}</td>
                      <td>{stat.ucl}</td>
                      <td>{stat.lcl}</td>
                      <td>{stat.zScoreMean}</td>
                      <td>{stat.zScoreVariance}</td>
                      <td>
                        <Form.Select
                          value={poolingSelections[stat.instrument] || 'Y'}
                          onChange={(e) => handleSelectionChange(stat.instrument, e.target.value)}
                          aria-label="Selection for Pooling"
                        >
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          value={assignForPooling[stat.instrument] || 'Y'}
                          onChange={(e) => handleAssignmentChange(stat.instrument, e.target.value)}
                          aria-label="Assign for Pooling"
                        >
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                        </Form.Select>
                      </td>
                    </tr>
                  ))}

                {/* Visual separator */}
                <tr className="pooled-values-separator">
                  <td colSpan="12" style={{ padding: '0.5rem', backgroundColor: '#f0f0f0' }}></td>
                </tr>

                {/* Pooled Values (all) row */}
                {pooledValuesAll && (
                  <tr className="pooled-values-all">
                    <td>
                      <strong>Pooled Values (all)</strong>
                    </td>
                    <td>{pooledValuesAll.n}</td>
                    <td>{pooledValuesAll.mean}</td>
                    <td>{pooledValuesAll.stdDev}</td>
                    <td>
                      {parseFloat(pooledValuesAll.mean) > 0
                        ? (
                            (parseFloat(pooledValuesAll.stdDev) /
                              parseFloat(pooledValuesAll.mean)) *
                            100
                          ).toFixed(2)
                        : 'N/A'}
                    </td>
                    <td>{pooledValuesAll.variance}</td>
                    <td>{pooledValuesAll.ucl}</td>
                    <td>{pooledValuesAll.lcl}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                )}

                {/* Pooled Values (selected) row */}
                {pooledValuesSelected && (
                  <tr className="pooled-values-selected">
                    <td>
                      <strong>Pooled Values (selected)</strong>
                    </td>
                    <td>{pooledValuesSelected.n}</td>
                    <td>{pooledValuesSelected.mean}</td>
                    <td>{pooledValuesSelected.stdDev}</td>
                    <td>
                      {parseFloat(pooledValuesSelected.mean) > 0
                        ? (
                            (parseFloat(pooledValuesSelected.stdDev) /
                              parseFloat(pooledValuesSelected.mean)) *
                            100
                          ).toFixed(2)
                        : 'N/A'}
                    </td>
                    <td>{pooledValuesSelected.variance}</td>
                    <td>{pooledValuesSelected.ucl}</td>
                    <td>{pooledValuesSelected.lcl}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                )}
              </>
            ) : (
              <tr>
                <td colSpan="12" className="text-center">
                  No instrument data available for the selected method and sample name
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {pooledValuesSelected && (
        <div className="d-flex justify-content-end mt-3">
          <Button
            variant="primary"
            disabled={isAssigning}
            onClick={() => {
              setIsAssigning(true);

              // Get all instruments that are marked 'Y' for assignForPooling
              const instrumentsToAssign = instrumentStats
                .filter((stat) => assignForPooling[stat.instrument] === 'Y')
                .map((stat) => stat.instrument);

              // Track promises to know when all operations are complete
              const updatePromises = [];

              // For each instrument that is selected for assignment
              instrumentsToAssign.forEach((instrument) => {
                // Create fullKey for the instrument
                const instrumentFullKey = [
                  location,
                  method,
                  sampleName,
                  instrument,
                  parameter
                ].join('|');

                // Get existing configuration ID for this instrument if it exists
                const configId = config[instrument] ? config[instrument].id : null;

                // Prepare configuration data
                let configData;

                // If there's no existing config, add default values
                if (!configId) {
                  configData = {
                    ...getDefaultControlStrategyConfiguration(),
                    recType: 'configurations',
                    fullKey: instrumentFullKey,
                    upperControlLimit: pooledValuesSelected.ucl,
                    lowerControlLimit: pooledValuesSelected.lcl,
                    controlStrategy: 'Control limits',
                    updatedAt: serverTimestamp()
                  };
                } else {
                  configData = {
                    fullKey: instrumentFullKey,
                    upperControlLimit: pooledValuesSelected.ucl,
                    lowerControlLimit: pooledValuesSelected.lcl,
                    controlStrategy: 'Control limits',
                    updatedAt: serverTimestamp()
                  };
                }

                // Update the configuration using the function from db.js and track the promise
                const updatePromise = updateConfiguration(configId, configData)
                  .then(() => {
                    console.log(
                      `Updated configuration for instrument: ${instrument} (${configId}): ${JSON.stringify(
                        configData,
                        null,
                        2
                      )}`
                    );
                  })
                  .catch((error) => {
                    console.error(
                      `Error updating configuration for instrument: ${instrument}`,
                      error
                    );
                  });

                updatePromises.push(updatePromise);
              });

              // When all updates are complete, set isAssigning to false
              Promise.all(updatePromises)
                .then(() => {
                  setIsAssigning(false);
                })
                .catch(() => {
                  setIsAssigning(false);
                });
            }}
          >
            {isAssigning ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                <span>Assigning...</span>
              </>
            ) : (
              'Assign Selected Limits'
            )}
          </Button>
        </div>
      )}
      <InstrumentPoolingDisplay
        data={data}
        location={location}
        method={method}
        sampleName={sampleName}
        parameter={parameter}
      />
    </div>
  );
};

export default InstrumentLimits;
